import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, atom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useDashboardDuplicate } from 'hooks/projectGroupEdit/useProjectGroupDuplication';
import { useFilterToolbarErrors, useResetFilterValues } from 'hooks/useFilterToolbar';
import { useInvalidateGroupConfigurations } from 'hooks/useGroupConfig';
import { useGroupData } from 'hooks/useGroupData';
import { useSaveGroup } from 'hooks/useSaveGroup';
import { useUpdateGroup } from 'hooks/useUpdateGroup';
import { useUser } from 'state/auth';
import {
  useDashboardGroupName,
  useDashboard,
  useDashboardGroupId,
  useDashboardTriggers,
  useDashboardGroupType,
  useDashboardStatus,
} from 'state/dashboard';
import { useAddressQuery, useCloneQuery } from 'state/queryParams';
import { GROUP_TYPE, NEW_GROUP_ID } from 'utils/constants';
import { buildDashboardURLWithParams } from 'utils/navigation';

/* Manages useLatestProjectStage field for ProjectGroupEdit form */
export const useLatestProjectStage = () => {
  const [savedTriggers, setSavedTriggers] = useDashboardTriggers();

  const resetProjectStages = () => {
    setSavedTriggers([]);
  };

  const hasSelectedProjectStages = useMemo(() => {
    let hasSelected = false;
    savedTriggers.forEach(({ documentTypes }) => {
      if (documentTypes.length) {
        hasSelected = true;
      }
    });

    return hasSelected;
  }, [savedTriggers]);

  return {
    savedTriggers,
    setSavedTriggers,
    resetProjectStages,
    hasSelectedProjectStages,
  };
};

export const useNavigateBackToDashboard = () => {
  const navigate = useNavigate();
  const [type] = useDashboardGroupType();

  const navigateBackToDashboard = useCallback(
    (groupId?: number) => {
      // Group successfully updated navigate to dashboard
      const url = buildDashboardURLWithParams({ groupId, groupType: type as GROUP_TYPE });
      navigate(url);
    },
    [type],
  );

  return { navigateBackToDashboard };
};

/* We need to manage saving state through jotai for now. In our current version
 * of jotai (1.6.6), we cannot share state of mutation, and must manage it
 * ourselves.
 */
const isSavingProjectGroupAtom = atom(false);

export const useIsSavingProjectGroup = () => useAtom(isSavingProjectGroupAtom);

/* Handles form submission and side effects */
export const useSubmitForm = () => {
  const [isSavingProjectGroup, setIsSavingProjectGroup] = useIsSavingProjectGroup();
  const { updateGroup } = useUpdateGroup();
  const { saveGroup } = useSaveGroup();
  const { navigateBackToDashboard } = useNavigateBackToDashboard();
  const [type] = useDashboardGroupType();
  const { user } = useUser();
  const { invalidateGroupConfigurations } = useInvalidateGroupConfigurations();

  const onSuccessCallback = (newGroupId: number) => {
    setIsSavingProjectGroup(false);
    invalidateGroupConfigurations(String(user?.id), type);
    navigateBackToDashboard(newGroupId);
  };

  const onErrorCallback = () => {
    setIsSavingProjectGroup(false);
  };

  const submitSaveGroup = useCallback(() => {
    if (isSavingProjectGroup) {
      return;
    }
    setIsSavingProjectGroup(true);
    saveGroup.mutate({ onSuccessCallback, onErrorCallback });
  }, [saveGroup, isSavingProjectGroup]);

  const submitUpdateGroup = useCallback(() => {
    if (isSavingProjectGroup) {
      return;
    }
    setIsSavingProjectGroup(true);
    updateGroup.mutate({ onSuccessCallback, onErrorCallback });
  }, [updateGroup, isSavingProjectGroup]);

  return {
    submitSaveGroup,
    submitUpdateGroup,
    isLoading: isSavingProjectGroup,
    navigateBackToDashboard,
  };
};

const LATEST_PROJECT_STAGE_ERROR_MESSAGE = 'At least 1 trigger is required';

const isGroupNameInvalidAtom = atom(false);

/* Manages the state of the form. Mainly deals with errors and validation. */
export const useFormState = () => {
  const [groupId] = useDashboardGroupId();
  const [groupName] = useDashboardGroupName();
  const { hasSelectedProjectStages } = useLatestProjectStage();
  const { setLatestStageFilterError } = useFilterToolbarErrors();
  const [isGroupNameInvalid, setIsGroupNameInvalid] = useAtom(isGroupNameInvalidAtom);
  const [groupType] = useDashboardGroupType();

  const isCreatingNewGroup = useMemo(() => groupId === NEW_GROUP_ID, [groupId]);

  const resetFormState = useCallback(() => {
    // clear all errors
    setLatestStageFilterError('');
    setIsGroupNameInvalid(false);
  }, [setLatestStageFilterError, setIsGroupNameInvalid]);

  const validateProjectGroupForm = useCallback(() => {
    let isValid = true;
    if (!hasSelectedProjectStages && groupType === GROUP_TYPE.PROJECT) {
      setLatestStageFilterError(LATEST_PROJECT_STAGE_ERROR_MESSAGE);
      isValid = false;
    }

    if (!groupName) {
      setIsGroupNameInvalid(true);
      isValid = false;
    }

    if (!isValid) {
      return false;
    }

    // clear all errors
    setLatestStageFilterError('');
    setIsGroupNameInvalid(false);

    return true;
  }, [
    hasSelectedProjectStages,
    groupName,
    setLatestStageFilterError,
    setIsGroupNameInvalid,
    groupType,
  ]);

  return {
    isCreatingNewGroup,
    validateProjectGroupForm,
    isGroupNameInvalid,
    setIsGroupNameInvalid,
    resetFormState,
  };
};

/* Resets the ProjectGroup form when the group ID changes in the URL. */
export const useResetForm = (groupId: string | undefined) => {
  const { startNewProject } = useDashboard();
  const { resetFormState } = useFormState();
  const { resetFilterValuesForGroupChange } = useResetFilterValues();
  const { cloneQuery } = useCloneQuery();
  const isDuplicating = !!cloneQuery;
  const group = useGroupData(String(groupId));
  const [isDoneReset, setIsDoneReset] = useState(false);
  const [, setAddress] = useAddressQuery();
  const [, setStatus] = useDashboardStatus();

  useDashboardDuplicate(cloneQuery || '', isDuplicating);

  //  When the group ID changes, we should reset the form, unless we are duplicating a group.
  useEffect(() => {
    if (isDuplicating) {
      setIsDoneReset(true);
      setStatus('active');
      setAddress('');

      return;
    }
    resetFormState();
    if (groupId === NEW_GROUP_ID) {
      startNewProject(GROUP_TYPE.PROJECT);
    }
    setIsDoneReset(true);
  }, [groupId, isDuplicating]);

  useEffect(() => {
    if (group?.id && !isDuplicating) {
      resetFilterValuesForGroupChange(group);
    }
  }, [group?.id, isDuplicating, resetFilterValuesForGroupChange]);

  return { isDoneReset };
};
